/**
 * 制限酵素の切断と消化断片（§5.5 I67–I69）。環状は原点ラップ断片を出す。
 */

import type { Interval, Topology } from '../sequence/interval';
import { searchCodes, type PatternHit } from './patternSearch';
import {
  RESTRICTION_LIBRARY,
  findEnzyme,
  overhangKind,
  type OverhangKind,
  type RestrictionEnzyme,
} from './restrictionEnzymes';

export interface RestrictionCut {
  enzymeId: string;
  enzymeName: string;
  start: number;
  end: number;
  cut: number;
  strand: 1 | -1;
  overhang: OverhangKind;
  unique: boolean;
}

export interface RestrictionFragment {
  start: number;
  end: number;
  length: number;
  wrapped: boolean;
}

export function scanRestrictionCuts(
  codes: Uint8Array,
  options: {
    genomeLength: number;
    topology: Topology;
    interval: Interval;
    enzymeIds: string[];
    extraEnzymes?: RestrictionEnzyme[];
    limitPerEnzyme?: number;
  }
): RestrictionCut[] {
  const limit = options.limitPerEnzyme ?? 500;
  const cuts: RestrictionCut[] = [];
  const counts = new Map<string, number>();
  const extras = options.extraEnzymes ?? [];

  for (const id of options.enzymeIds) {
    const enzyme = findEnzyme(id, extras);
    if (!enzyme) continue;
    const hits = searchCodes(codes, {
      genomeLength: options.genomeLength,
      topology: options.topology,
      interval: options.interval,
      pattern: enzyme.pattern,
      bothStrands: false,
      limit,
    });
    counts.set(enzyme.id, hits.length);
    for (const hit of hits) {
      cuts.push(cutFromHit(enzyme, hit, options.genomeLength));
    }
  }

  for (const cut of cuts) {
    cut.unique = (counts.get(cut.enzymeId) ?? 0) === 1;
  }
  cuts.sort((a, b) => a.cut - b.cut || a.enzymeName.localeCompare(b.enzymeName));
  return cuts;
}

function cutFromHit(enzyme: RestrictionEnzyme, hit: PatternHit, genomeLength: number): RestrictionCut {
  let cut = hit.start + enzyme.cutAfter;
  if (cut >= genomeLength) cut -= genomeLength;
  if (cut < 0) cut += genomeLength;
  return {
    enzymeId: enzyme.id,
    enzymeName: enzyme.name,
    start: hit.start,
    end: hit.end > genomeLength ? hit.end : hit.end,
    cut,
    strand: hit.strand,
    overhang: overhangKind(enzyme),
    unique: false,
  };
}

export function digestFragments(
  cuts: RestrictionCut[],
  genomeLength: number,
  topology: Topology
): RestrictionFragment[] {
  const positions = [...new Set(cuts.map((c) => c.cut))].sort((a, b) => a - b);
  if (positions.length === 0) {
    return [{ start: 0, end: genomeLength, length: genomeLength, wrapped: false }];
  }
  const fragments: RestrictionFragment[] = [];
  if (topology === 'circular') {
    for (let i = 0; i < positions.length; i += 1) {
      const start = positions[i];
      const end = i + 1 < positions.length ? positions[i + 1] : positions[0] + genomeLength;
      const length = end - start;
      fragments.push({
        start,
        end,
        length,
        wrapped: end > genomeLength,
      });
    }
  } else {
    if (positions[0] > 0) {
      fragments.push({ start: 0, end: positions[0], length: positions[0], wrapped: false });
    }
    for (let i = 0; i < positions.length - 1; i += 1) {
      const start = positions[i];
      const end = positions[i + 1];
      fragments.push({ start, end, length: end - start, wrapped: false });
    }
    const last = positions[positions.length - 1];
    if (last < genomeLength) {
      fragments.push({
        start: last,
        end: genomeLength,
        length: genomeLength - last,
        wrapped: false,
      });
    }
  }
  return fragments.sort((a, b) => b.length - a.length);
}

export function silentSiteSuggestions(
  codes: Uint8Array,
  options: {
    genomeLength: number;
    topology: Topology;
    interval: Interval;
    selectedIds: string[];
  }
): RestrictionEnzyme[] {
  const selected = new Set(options.selectedIds.map((id) => id.toLowerCase()));
  return RESTRICTION_LIBRARY.filter((enzyme) => {
    if (selected.has(enzyme.id)) return false;
    const hits = searchCodes(codes, {
      genomeLength: options.genomeLength,
      topology: options.topology,
      interval: options.interval,
      pattern: enzyme.pattern,
      bothStrands: false,
      limit: 1,
    });
    return hits.length === 0;
  }).slice(0, 12);
}
